import AuthService from '../services/AuthService';
import { API_BASE } from '../constants/api';

const api = async (url, options = {}) => {
        const isFormData = options.body instanceof FormData;
        const headers = {
                ...options.headers,
                ...(!isFormData && { 'Content-Type': 'application/json' }),
        };

        let response = await fetch(`${API_BASE}${url}`, {
                ...options,
                headers,
                credentials: 'include'
        });

        if (response.status === 401) {
                try {
                        await AuthService.refreshToken();
                        response = await fetch(`${API_BASE}${url}`, {
                                ...options,
                                headers,
                                credentials: 'include'
                        });
                } catch (error) {
                        window.location.href = '/login';
                        throw error;
                }
        }

        return response;
};

const addDevice = async (deviceData) => {
        return await api('/devices', {
                method: 'POST',
                body: JSON.stringify(deviceData),
        });
};

const updateDevice = async (deviceId, deviceData) => {
        return await api(`/devices/${deviceId}`, {
                method: 'PUT',
                body: JSON.stringify(deviceData),
        });
};

const deleteDevice = async (deviceId) => {
        return await api(`/devices/${deviceId}`, {
                method: 'DELETE',
        });
};

const getCustomer = async (customerId) => {
        return await api(`/customers/${customerId}`);
};

const getCustomers = async () => {
        return await api('/customers');
};

const createCustomer = async (customerData) => {
        return await api('/customers', {
                method: 'POST',
                body: JSON.stringify(customerData),
        });
};

const getDevice = async (deviceId) => {
        return await api(`/devices/${deviceId}`);
};

// getDeviceData returns a device's measurements. range is one of the keys in
// HISTORY_RANGES; omitting it leaves the server on its default 7-day window.
// Anything longer comes back bucketed, so the point count stays modest.
const getDeviceData = async (deviceId, range) => {
        const query = range ? `?range=${encodeURIComponent(range)}` : '';
        return await api(`/sensor/${deviceId}${query}`);
};

const getDeviceDailyMetrics = async (deviceId) => {
        return await api(`/sensor-daily-metrics/${deviceId}`);
};

// getDeviceBatterySignal returns only the battery and signal series, bucketed
// server-side for the longer ranges, plus first_reading_at so the caller knows
// how far back the device actually reported. range is one of the keys in
// HISTORY_RANGES.
const getDeviceBatterySignal = async (deviceId, range) => {
        return await api(`/sensor/${deviceId}/battery-signal?range=${encodeURIComponent(range)}`);
};

// getMoistureForecast reports whether the device's soil is on track to fall below
// what its family tolerates within the next few days. It is a warning only: it
// raises no alert, sends no email and does not make the device unhealthy.
const getMoistureForecast = async (deviceId) => {
        return await api(`/devices/${deviceId}/moisture-forecast`);
};

const getPlantProfileDevices = async (id) => {
        return await api(`/plant-profiles/${id}/devices`);
};

// previewPlantProfileDevices grades the family's devices for one metric against
// parameters that have not been saved yet. body: { metric, parameters, device_ids },
// where metric is one of PREVIEWABLE_METRICS.
const previewPlantProfileDevices = async (id, body) => {
        return await api(`/plant-profiles/${id}/device-preview`, {
                method: 'POST',
                body: JSON.stringify(body)
        });
};

const getPlantProfileOverlays = async () => {
        return await api('/plant-profile-overlays');
};

const getSites = async () => {
        return await api('/me/sites');
};

const createSite = async (siteData) => {
        return await api('/sites', {
                method: 'POST',
                body: JSON.stringify(siteData),
        });
};

const createRoom = async (roomData) => {
        return await api('/rooms', {
                method: 'POST',
                body: JSON.stringify(roomData),
        });
};

const updateSite = async (siteId, siteData) => {
        return await api(`/sites/${siteId}`, {
                method: 'PUT',
                body: JSON.stringify(siteData),
        });
};

const deleteSite = async (siteId) => {
        return await api(`/sites/${siteId}`, {
                method: 'DELETE',
        });
};

const updateRoom = async (roomId, roomData) => {
        return await api(`/rooms/${roomId}`, {
                method: 'PUT',
                body: JSON.stringify(roomData),
        });
};

const deleteRoom = async (roomId) => {
        return await api(`/rooms/${roomId}`, {
                method: 'DELETE',
        });
};

const uploadDeviceImage = async (deviceId, formData) => {
        return await api(`/devices/${deviceId}/image`, {
                method: 'POST',
                body: formData,
        });
};

const getFloorPlan = async (roomId) => {
        return await api(`/rooms/${roomId}/floor-plan`);
};

// The image URL is versioned by updated_at so a replaced plan is never served
// from cache.
const getFloorPlanImageUrl = (roomId, version) =>
        `${API_BASE}/rooms/${roomId}/floor-plan/image?v=${encodeURIComponent(version)}`;

const uploadFloorPlan = async (roomId, formData) => {
        return await api(`/rooms/${roomId}/floor-plan`, {
                method: 'POST',
                body: formData,
        });
};

const deleteFloorPlan = async (roomId) => {
        return await api(`/rooms/${roomId}/floor-plan`, {
                method: 'DELETE',
        });
};

// Pass x = y = null to take the device off the floor plan.
const setDevicePosition = async (deviceId, x, y) => {
        return await api(`/devices/${deviceId}/position`, {
                method: 'PUT',
                body: JSON.stringify({ x, y }),
        });
};

// Mark one of the device's open alerts as seen. There is no undo.
const acknowledgeAlert = async (deviceId, alertId) => {
        return await api(`/devices/${deviceId}/alerts/${alertId}/acknowledge`, {
                method: 'PUT',
        });
};

const deleteDeviceImage = async (imageId) => {
        return await api(`/images/${imageId}`, {
                method: 'DELETE',
        });
};

const changePassword = async (passwordData) => {
        return await api('/me/password', {
                method: 'PUT',
                body: JSON.stringify(passwordData),
        });
};

const updateUserProfile = async (profileData) => {
        return await api('/me', {
                method: 'PUT',
                body: JSON.stringify(profileData),
        });
};

const getUserSettings = async () => {
        return await api('/me/settings');
};

const updateUserSettings = async (settingsData) => {
        return await api('/me/settings', {
                method: 'PUT',
                body: JSON.stringify(settingsData),
        });
};

const getUserSettingsForUser = async (userId) => {
        return await api(`/users/${userId}/settings`);
};

const updateUserSettingsForUser = async (userId, settingsData) => {
        return await api(`/users/${userId}/settings`, {
                method: 'PUT',
                body: JSON.stringify(settingsData),
        });
};

const getMonthlyReports = async (customerId) => {
        return await api(`/customers/${customerId}/reports`);
};

// downloadMonthlyReport fetches a stored report and hands it to the browser as
// a file download. Resolves to false when the request failed.
const downloadMonthlyReport = async (customerId, period) => {
        const response = await api(`/customers/${customerId}/reports/${period}`);
        if (!response.ok) return false;
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `cleverplant-report-${period}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
        return true;
};

const getCustomerLogo = async (customerId) => {
        return await api(`/customers/${customerId}/logo`);
};

// The image URL is versioned by updated_at so a replaced logo is never served
// from cache.
const getCustomerLogoImageUrl = (customerId, version) =>
        `${API_BASE}/customers/${customerId}/logo/image?v=${encodeURIComponent(version)}`;

const uploadCustomerLogo = async (customerId, formData) => {
        return await api(`/customers/${customerId}/logo`, {
                method: 'POST',
                body: formData,
        });
};

const deleteCustomerLogo = async (customerId) => {
        return await api(`/customers/${customerId}/logo`, {
                method: 'DELETE',
        });
};

const getSitesForUser = async (userId) => {
        return await api(`/users/${userId}/sites`);
};

const getUsers = async () => {
        return await api('/users');
};

const getUsersByCustomer = async (customerId) => {
        return await api(`/customers/${customerId}/users`);
};

const createUser = async (userData) => {
        return await api('/users', {
                method: 'POST',
                body: JSON.stringify(userData),
        });
};

const updateUserPassword = async (userId, passwordData) => {
        return await api(`/users/${userId}/password`, {
                method: 'PUT',
                body: JSON.stringify(passwordData),
        });
};

const updateUserRole = async (userId, roleId) => {
        return await api(`/users/${userId}/role`, {
                method: 'PUT',
                body: JSON.stringify({ role_id: roleId }),
        });
};

const deleteUser = async (userId) => {
        return await api(`/users/${userId}`, {
                method: 'DELETE',
        });
};

const getDevicesWithUpdates = async () => {
        return await api('/devices/updates');
};

const getDevicesWithUpdatesByCustomer = async (customerId) => {
        return await api(`/customers/${customerId}/devices/updates`);
};

const updateDeviceFirmwares = async (updates) => {
        return await api('/devices/updates', {
                method: 'PUT',
                body: JSON.stringify(updates),
        });
};

const getLogs = async () => {
        return await api('/logs');
};

// Alert rules with the points each takes off a plant's score (superadmin).
const getAlertRules = async () => {
        return await api('/alert-rules');
};

const setAlertRuleScoreWeight = async (ruleId, scoreWeight) => {
        return await api(`/alert-rules/${ruleId}/score-weight`, {
                method: 'PUT',
                body: JSON.stringify({ score_weight: scoreWeight }),
        });
};

const forgotPassword = async (email) => {
        return await fetch(`${API_BASE}/password/forgot`, {
                method: 'POST',
                headers: {
                        'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email }),
        });
};

const resetPassword = async (token, newPassword) => {
        return await fetch(`${API_BASE}/password/reset`, {
                method: 'POST',
                headers: {
                        'Content-Type': 'application/json',
                },
                body: JSON.stringify({ token, password: newPassword }),
        });
};

const getFirmwares = async () => {
        return await api('/firmwares');
};

const getFirmware = async (firmwareId) => {
        return await api(`/firmwares/${firmwareId}`);
};

const uploadFirmware = async (formData) => {
        return await api('/firmwares', {
                method: 'POST',
                body: formData,
        });
};

const deleteFirmware = async (firmwareId) => {
        return await api(`/firmwares/${firmwareId}`, {
                method: 'DELETE',
        });
};

const submitSupportRequest = async (data) => {
        return await api('/support', {
                method: 'POST',
                body: JSON.stringify(data),
        });
};

const getPlantProfiles = async () => {
        return await api('/plant-profiles');
};

const createPlantProfile = async (data) => {
        return await api('/plant-profiles', {
                method: 'POST',
                body: JSON.stringify(data),
        });
};

const getPlantProfile = async (profileId) => {
        return await api(`/plant-profiles/${profileId}`);
};

const updatePlantProfile = async (profileId, data) => {
        return await api(`/plant-profiles/${profileId}`, {
                method: 'PUT',
                body: JSON.stringify(data),
        });
};

const updatePlantFamily = async (profileId, data) => {
        return await api(`/plant-profiles/${profileId}/details`, {
                method: 'PUT',
                body: JSON.stringify(data),
        });
};

const deletePlantProfile = async (profileId) => {
        return await api(`/plant-profiles/${profileId}`, {
                method: 'DELETE',
        });
};

export {
        getAlertRules,
        setAlertRuleScoreWeight,
        api,
        addDevice,
        updateDevice,
        deleteDevice,
        getCustomer,
        getCustomers,
        createCustomer,
        getDevice,
        getDeviceData,
        getDeviceDailyMetrics,
        getDeviceBatterySignal,
        getMoistureForecast,
        getSites,
        createSite,
        createRoom,
        updateSite,
        deleteSite,
        updateRoom,
        deleteRoom,
        uploadDeviceImage,
        deleteDeviceImage,
        getFloorPlan,
        getFloorPlanImageUrl,
        uploadFloorPlan,
        deleteFloorPlan,
        setDevicePosition,
        acknowledgeAlert,
        changePassword,
        updateUserProfile,
        getUserSettings,
        updateUserSettings,
        getUserSettingsForUser,
        updateUserSettingsForUser,
        getMonthlyReports,
        downloadMonthlyReport,
        getCustomerLogo,
        getCustomerLogoImageUrl,
        uploadCustomerLogo,
        deleteCustomerLogo,
        getSitesForUser,
        getUsers,
        getUsersByCustomer,
        createUser,
        updateUserPassword,
        updateUserRole,
        deleteUser,
        getDevicesWithUpdates,
        getDevicesWithUpdatesByCustomer,
        updateDeviceFirmwares,
        getLogs,
        forgotPassword,
        resetPassword,
        getFirmwares,
        getFirmware,
        uploadFirmware,
        deleteFirmware,
        submitSupportRequest,
        getPlantProfiles,
        createPlantProfile,
        getPlantProfile,
        updatePlantProfile,
        updatePlantFamily,
        deletePlantProfile,
        getPlantProfileOverlays,
        getPlantProfileDevices,
        previewPlantProfileDevices
};
